import { axios } from "../helpers/axios";
import { doesUserHasAccess } from "../helpers/utils";

const rejectAccess = () => Promise.reject(new Error("Access Denied"));

// API for fetching all notifications
export const getAllNotificationsAPI = ({
  current,
  limit_to,
  is_read,
  is_starred,
}: any) => {
  if (!doesUserHasAccess(172)) return rejectAccess();
  let url = `notifications/get_all_notifications/?current=${current}&limit_to=${limit_to}`;

  // Append is_read if it exists (explicit check for undefined because false/0 is valid)
  if (is_read !== undefined && is_read !== null) {
    url += `&is_read=${is_read}`;
  }

  // Append is_starred if it exists
  if (is_starred !== undefined && is_starred !== null) {
    url += `&is_starred=${is_starred}`;
  }

  return axios.get(url);
};

// API for marking a single notification as read
export const markNotificationReadAPI = (notification_id: any) => {
  if (!doesUserHasAccess(172)) return rejectAccess();
  return axios.patch(
    `notifications/mark_notification_read/${notification_id}/`
  );
};

// API for marking all notifications as read
export const markAllReadAPI = () => {
  if (!doesUserHasAccess(172)) return rejectAccess();
  return axios.post(`notifications/mark_all_read/`);
};

// API for clearing all notifications
export const clearAllNotificationsAPI = () => {
  return axios.post(`notifications/clear_all_notifications/`);
};

// API to get organization preferences
export const getOrganizationPreferencesAPI = (organization_name: string) => {
  if (!doesUserHasAccess(173)) return rejectAccess();
  return axios.get(
    `notifications/get_organization_preferences/?organization_name=${organization_name}`,
    {
      suppressAlerts: { statusCode: [400] },
    } as any
  );
};

// API to toggle a main notification category (enable/disable)
export const toggleNotificationCategoryAPI = (payload: {
  organization_name: string;
  category: string;
}) => {
  if (!doesUserHasAccess(173)) return rejectAccess();
  return axios.patch(`notifications/toggle_notification_category/`, payload);
};

// API to toggle a specific subcategory event
export const toggleNotificationSubcategoryAPI = (payload: {
  organization_name: string;
  category: string;
  subcategory_key: string;
}) => {
  if (!doesUserHasAccess(173)) return rejectAccess();
  return axios.patch(`notifications/toggle_notification_subcategory/`, payload);
};

// API to add a recipient to a category
export const addRecipientToCategoryAPI = (payload: {
  organization_name: string;
  category: string;
  recipient_data: any[];
}) => {
  if (!doesUserHasAccess(173)) return rejectAccess();
  return axios.post(`notifications/add_recipient_to_category/`, payload);
};

// API to get recipients for a specific category
// Note: Since this is a GET request with a payload, axios uses the 'params' property
export const getRecipientsFromCategoryAPI = (params: {
  organization_name: string;
  category: string;
  is_livis_user?: boolean;
}) => {
  if (!doesUserHasAccess(173)) return rejectAccess();
  return axios.get(`notifications/get_recipients_from_category/`, { params });
};

// API to remove recipients from a category
// Note: DELETE requests send data via the 'data' property in the config object
export const removeRecipientsFromCategoryAPI = (payload: {
  organization_name: string;
  category: string;
  recipient_emails: string[];
}) => {
  if (!doesUserHasAccess(173)) return rejectAccess();
  return axios.delete(`notifications/remove_recipients_from_category/`, {
    data: payload,
  });
};

export const importRecipientsFromCategoryAPI = (payload: {
  organization_name: string;
  source_category: string;
  target_category: string;
}) => {
  if (!doesUserHasAccess(173)) return rejectAccess();
  return axios.post(`notifications/import_recipients_from_category/`, payload);
};

// API to toggle notification star status
export const toggleNotificationStarAPI = (notification_id: string) => {
  if (!doesUserHasAccess(172)) return rejectAccess();
  return axios.patch(
    `notifications/toggle_notification_star/${notification_id}/`
  );
};

// API to fetch LIVIS users for dropdown population
export const getLivisUsersAPI = (params: { organization_name: string }) => {
  return axios.get(`notifications/get_livis_users/`, { params });
};

export const createAlertTriggersAPI = (payload: {
  organization_name: string;
  trigger_event: string;
  severity: string;
  thresholds_value: number[];
}) => {
  if (!doesUserHasAccess(174)) return rejectAccess();
  return axios.post(`notifications/create_alert_triggers/`, payload);
};

// API to edit alert triggers
export const editAlertTriggersAPI = (payload: {
  organization_name: string;
  trigger_event: string;
  severity: string;
  thresholds_value: number[];
}) => {
  if (!doesUserHasAccess(174)) return rejectAccess();
  return axios.patch(`notifications/edit_alert_triggers/`, payload);
};

// API to pause/disable alert triggers
export const pauseAlertTriggersAPI = (payload: {
  organization_name: string;
  trigger_event: string;
}) => {
  if (!doesUserHasAccess(174)) return rejectAccess();
  return axios.patch(`notifications/pause_alert_triggers/`, payload);
};

// API to get alert trigger preferences
export const getAlertTriggerPreferencesAPI = (organization_name: string) => {
  if (!doesUserHasAccess(174)) return rejectAccess();
  return axios.get(`notifications/get_alert_trigger_preferences/`, {
    params: { organization_name },
    suppressAlerts: { statusCode: [400] },
  } as any);
};

// API to fetch all triggered alerts
export const getAllAlertsAPI = (params: { sort?: number } = { sort: -1 }) => {
  if (!doesUserHasAccess(172)) return rejectAccess();
  return axios.get(`notifications/get_all_alerts/`, { params });
};

// API to update report digest frequency
// DIGEST_FREQUENCY_OPTIONS = ['daily', 'weekly', 'monthly', 'yearly']
export const updateReportDigestFrequencyAPI = (payload: {
  organization_name: string;
  report_frequency: string[];
}) => {
  if (!doesUserHasAccess(173)) return rejectAccess();
  return axios.patch(`notifications/update_report_digest_frequency/`, payload);
};
